/** @jsxImportSource @emotion/react */
import Dropdown from 'components/atoms/Dropdown'
import Link from 'components/atoms/Link'
import Pagination from 'components/atoms/Pagination'
import { Popover } from 'components/atoms/Popover'
import Toast from 'components/atoms/Toast'
import { TooltipWrapper } from 'components/atoms/TooltipWrapper'
import { StatePill } from 'components/lab/atoms/StatePill'
import routes from 'components/lab/constants/routes'
import { reportSendResult } from 'components/lab/billingPatientStatements/sendResultToast'
import {
  formatDate,
  formatDateTime,
  formatMoney,
} from 'components/lab/billingPatientStatements/statementFormat'
import React from 'react'
import {
  billingStatementCycleStatusLabel,
  billingStatementCycleStepLabel,
  BILLING_STATEMENT_CYCLE_STATUS_LABEL,
  BillingPatientStatementDeliveryMethod,
  BillingStatementCycleStatus,
  OutstandingPatientBalanceItem,
} from 'store/types'
import {
  useSendPatientStatementsMutation,
  useGetOutstandingPatientBalancesQuery,
  useLazyGetOutstandingPatientBalanceIdsQuery,
  useLazyGetOutstandingPatientBalancesQuery,
  useLazyGetStatementDownloadQuery,
  useLazyPrintOutstandingBalancesSummaryQuery,
  useSendPatientStatementMutation,
  useSetStatementCycleStatusMutation,
} from 'store/services/patientStatements'
import { useDebounce } from 'use-debounce'

type BalanceType = 'ALL' | 'OWING' | 'REFUND_DUE'

type CycleFilter = 'ALL' | BillingStatementCycleStatus | 'PAPER_DUE'

// The colour a cycle status wears in the listing: settled and on-plan read as
// calm, everything past day 60 as something a biller has to act on.
const CYCLE_STATUS_COLOR: Record<string, string> = {
  [BillingStatementCycleStatus.ACTIVE]: '#3CAEFF',
  [BillingStatementCycleStatus.COLLECTIONS_REVIEW]: '#fd7e14',
  [BillingStatementCycleStatus.PAYMENT_PLAN]: '#6f42c1',
  [BillingStatementCycleStatus.COLLECTIONS]: '#dc3545',
  [BillingStatementCycleStatus.PAID]: '#28a745',
  [BillingStatementCycleStatus.CLOSED]: '#6c757d',
}

// What a biller may set by hand. A cycle is never marked paid from here — that
// is what money arriving does.
const CYCLE_STATUS_ACTIONS: BillingStatementCycleStatus[] = [
  BillingStatementCycleStatus.ACTIVE,
  BillingStatementCycleStatus.PAYMENT_PLAN,
  BillingStatementCycleStatus.COLLECTIONS_REVIEW,
  BillingStatementCycleStatus.COLLECTIONS,
  BillingStatementCycleStatus.CLOSED,
]

const SENT_BADGE_CSS = {
  background: '#28a745',
  color: '#fff',
  borderRadius: 6,
  padding: '0.15rem 0.4rem',
  fontSize: 12,
}

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

const csvCell = (value: string | number | null | undefined) => {
  const text = value === null || value === undefined ? '' : String(value)
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

// Payments 4.5 — the biller's account-wide worklist: every patient owing a
// balance or owed a refund, with credit that is still spoken for shown on hold.
// Statements are sent and exported from here; paper goes through Ready to Print.
export const OutstandingBalancesListing: React.FC = () => {
  const [page, setPage] = React.useState(0)
  const [pageSize, setPageSize] = React.useState(20)
  const [firstName, setFirstName] = React.useState('')
  const [lastName, setLastName] = React.useState('')
  const [balanceType, setBalanceType] = React.useState<BalanceType>('ALL')
  const [notSentDays, setNotSentDays] = React.useState('')
  const [cycleFilter, setCycleFilter] = React.useState<CycleFilter>('ALL')
  // statementId -> patientId. A plain Set of ids was enough while selection
  // never left the current page; selecting across pages means the rows a batch
  // send has to group by patient are no longer all on screen.
  const [selected, setSelected] = React.useState<Map<string, number>>(new Map())
  const [isSelectingAll, setIsSelectingAll] = React.useState(false)
  const [activeRowPopover, setActiveRowPopover] = React.useState<number | null>(null)
  const [activeBatchPopover, setActiveBatchPopover] = React.useState<boolean>(false)
  const [isExporting, setIsExporting] = React.useState(false)
  const [isPrintingSummary, setIsPrintingSummary] = React.useState(false)

  const [debouncedFirstName] = useDebounce(firstName, 300)
  const [debouncedLastName] = useDebounce(lastName, 300)
  const [debouncedNotSentDays] = useDebounce(notSentDays, 300)

  const filters = React.useMemo(() => ({
    patientFirstName: debouncedFirstName.trim() || undefined,
    patientLastName: debouncedLastName.trim() || undefined,
    balanceType,
    notSentDays: debouncedNotSentDays.trim() ? Number(debouncedNotSentDays) : undefined,
    // One control, two filters: a cycle state, or the paper waiting to be
    // printed regardless of which state asked for it.
    cycleStatus:
      cycleFilter === 'ALL' || cycleFilter === 'PAPER_DUE' ? undefined : cycleFilter,
    paperDue: cycleFilter === 'PAPER_DUE' ? true : undefined,
  }), [debouncedFirstName, debouncedLastName, balanceType, debouncedNotSentDays, cycleFilter])

  const { data, isFetching, refetch } = useGetOutstandingPatientBalancesQuery({
    page,
    size: pageSize,
    ...filters,
  })

  const [fetchOutstandingIds] = useLazyGetOutstandingPatientBalanceIdsQuery()
  // The CSV export needs whole rows, so it still reads the listing page by page.
  const [fetchOutstandingPage] = useLazyGetOutstandingPatientBalancesQuery()
  const [sendStatement] = useSendPatientStatementMutation()
  const [sendStatements, { isLoading: isSending }] = useSendPatientStatementsMutation()
  const [setCycleStatus] = useSetStatementCycleStatusMutation()
  const [getDownloadUrl] = useLazyGetStatementDownloadQuery()
  const [printOutstandingSummary] = useLazyPrintOutstandingBalancesSummaryQuery()

  React.useEffect(() => {
    setPage(0)
    setSelected(new Map())
    setActiveBatchPopover(false)
  }, [filters])

  const selectable = (data?.items ?? []).filter((item: OutstandingPatientBalanceItem) => item.statementId)
  const pageFullySelected = !!selectable.length
    && selectable.every((item: OutstandingPatientBalanceItem) => selected.has(item.statementId!))

  /**
   * The header checkbox selects every patient the current filters match, not
   * just the page in view — a worklist is worked as a whole. One request for
   * the ids behind the filters; unchecking clears the lot.
   */
  const toggleSelectAll = async () => {
    if (selected.size) {
      setSelected(new Map())
      return
    }

    if (!selectable.length) return

    setIsSelectingAll(true)

    try {
      // Patients with no statement yet have nothing to send or mark, and the
      // endpoint leaves them out for the same reason the rows have no checkbox.
      const matches = await fetchOutstandingIds(filters).unwrap()

      setSelected(new Map(matches.map((match) => [match.statementId, match.patientId])))
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message || 'Unable to select every patient'
      Toast({ type: 'error', label: message })
    } finally {
      setIsSelectingAll(false)
    }
  }

  const toggleRow = (id: string, patientId: number) => {
    setSelected((prev) => {
      const next = new Map(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.set(id, patientId)
      }
      return next
    })
  }

  const renderDelivery = (item: OutstandingPatientBalanceItem) => {
    if (!item.statementId) {
      return <span className="textGray">No statement yet</span>
    }

    if (!item.deliveryMethods?.length && !item.lastMailedAt) {
      return '--'
    }

    return (
      <div css={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        {/* Paper is shown with its confirmed date instead. */}
        {(item.deliveryMethods || [])
          .filter((method) => method !== BillingPatientStatementDeliveryMethod.MAIL)
          .map((method) => (
            <div key={method} css={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>{method.toUpperCase()}</span>
              <span css={SENT_BADGE_CSS}>{item.lastSentAt ? 'Sent' : 'Ready'}</span>
            </div>
          ))}
        {item.lastMailedAt && (
          <div css={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span>PAPER</span>
            <TooltipWrapper text={`Mailed ${formatDate(item.lastMailedAt)}`} show>
              <span css={{ ...SENT_BADGE_CSS, cursor: 'help' }}>Mailed</span>
            </TooltipWrapper>
          </div>
        )}
      </div>
    )
  }

  // Where the balance is in the cycle: the state it is in, what the patient
  // last heard, and what is coming. A paper statement nobody has mailed yet is
  // the one thing here that needs a biller, so it says so.
  const renderCycle = (item: OutstandingPatientBalanceItem) => {
    if (!item.cycleStatus) {
      return <span className="textGray">Not in a cycle</span>
    }

    return (
      <div css={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <StatePill
          color={CYCLE_STATUS_COLOR[item.cycleStatus] || '#6c757d'}
          label={billingStatementCycleStatusLabel(item.cycleStatus)}
        />
        {item.paperDueAt && (
          <Link
            to={`${routes.index}${routes.billing.root}${routes.billing.paperStatements}`}
            style={{ color: '#fd7e14', fontWeight: 600, fontSize: 12 }}>
            Paper due {formatDate(item.paperDueAt)}
          </Link>
        )}
        <span className="textGray" css={{ fontSize: 12 }}>
          Last: {billingStatementCycleStepLabel(item.cycleLastStep)}
        </span>
        <span className="textGray" css={{ fontSize: 12 }}>
          Next: {item.cycleNextStep
            ? `${billingStatementCycleStepLabel(item.cycleNextStep)} (${formatDate(item.cycleNextStepAt)})`
            : '--'}
        </span>
      </div>
    )
  }

  const handleSetCycleStatus = async (
    item: OutstandingPatientBalanceItem,
    status: BillingStatementCycleStatus,
  ) => {
    try {
      await setCycleStatus({ patientId: item.patientId, status }).unwrap()
      Toast({
        type: 'success',
        label: `Cycle set to ${BILLING_STATEMENT_CYCLE_STATUS_LABEL[status]}`,
      })
      setActiveRowPopover(null)
      refetch()
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message || 'Unable to update the cycle'
      Toast({ type: 'error', label: message })
    }
  }

  const handleSend = async (
    item: OutstandingPatientBalanceItem,
    method: BillingPatientStatementDeliveryMethod,
  ) => {
    try {
      await sendStatement({
        patientId: item.patientId,
        statementId: item.statementId!,
        deliveryMethods: [method],
      }).unwrap()
      Toast({ type: 'success', label: 'Statement sent' })
      setActiveRowPopover(null)
      refetch()
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message || 'Unable to send statement'
      Toast({ type: 'error', label: message })
    }
  }

  const handleBatchSend = async (method: BillingPatientStatementDeliveryMethod) => {
    // Closed before the request, not after: a dropdown left open over a
    // multi-second send invites a second click and a second send.
    setActiveBatchPopover(false)

    try {
      // The whole selection in one request, however many patients it spans —
      // and from the selection, not the page in view, so a selection spanning
      // pages is not quietly cut to the rows on screen. The server isolates
      // each statement: a patient who opted out of SMS comes back in `skipped`
      // instead of stopping everyone after them.
      const result = await sendStatements({
        statementIds: [...selected.keys()],
        deliveryMethods: [method],
      }).unwrap()

      reportSendResult(result)
      setSelected(new Map())
      refetch()
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message || 'Unable to send statements'
      Toast({ type: 'error', label: message })
    }
  }

  const sendSelectedLabel = () => {
    if (isSending) return 'Sending…'
    if (isSelectingAll) return 'Selecting…'
    return `Send Selected${selected.size ? ` (${selected.size})` : ''}`
  }

  const handleDownload = async (item: OutstandingPatientBalanceItem) => {
    try {
      const link = await getDownloadUrl({
        statementId: item.statementId!,
        patientId: item.patientId,
      }).unwrap()

      if (link.url) {
        const printWindow = window.open(link.url, '_blank')
        printWindow?.focus()
      }
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message || 'Unable to download'
      Toast({ type: 'error', label: message })
    }
  }

  // Every patient the current filters match, on one page.
  const handlePrintSummary = async () => {
    setIsPrintingSummary(true)

    try {
      const blob = await printOutstandingSummary(filters).unwrap()
      downloadBlob(blob, `patient-statements-summary-${Date.now()}.pdf`)
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message || 'Unable to print summary'
      Toast({ type: 'error', label: message })
    } finally {
      setIsPrintingSummary(false)
    }
  }

  // Exports the whole filtered listing, not just the visible page — pages are
  // walked at the server's maximum page size.
  const handleExportCsv = async () => {
    setIsExporting(true)

    try {
      const rows: OutstandingPatientBalanceItem[] = []
      let exportPage = 0
      let totalPages = 1

      while (exportPage < totalPages) {
        const result = await fetchOutstandingPage({ page: exportPage, size: 100, ...filters }).unwrap()
        rows.push(...(result.items ?? []))
        totalPages = result.totalPages || 1
        exportPage += 1
      }

      const header = ['Patient', 'Patient Balance', 'Unapplied Credit', 'Refund Due', 'Refund On Hold', 'Cycle Status', 'Last Step', 'Next Step', 'Paper Due', 'Statement Due Date', 'Statement Last Sent', 'Last Mailed']
      const lines = [
        header.join(','),
        ...rows.map((item) => [
          csvCell([item.patientLastName, item.patientFirstName].filter(Boolean).join(', ')),
          csvCell(item.patientBalance.toFixed(2)),
          csvCell(item.unappliedAmount.toFixed(2)),
          csvCell(item.refundDue.toFixed(2)),
          csvCell(item.refundHeld ? 'Yes' : 'No'),
          csvCell(item.cycleStatus ? billingStatementCycleStatusLabel(item.cycleStatus) : ''),
          csvCell(item.cycleLastStep ? billingStatementCycleStepLabel(item.cycleLastStep) : ''),
          csvCell(item.cycleNextStep ? billingStatementCycleStepLabel(item.cycleNextStep) : ''),
          csvCell(item.paperDueAt ? formatDate(item.paperDueAt) : ''),
          csvCell(item.statementDueDate ? formatDate(item.statementDueDate) : ''),
          csvCell(item.lastSentAt ? formatDateTime(item.lastSentAt) : ''),
          csvCell(item.lastMailedAt ? formatDate(item.lastMailedAt) : ''),
        ].join(',')),
      ]

      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `outstanding-balances-${balanceType.toLowerCase()}-${Date.now()}.csv`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message || 'Unable to export CSV'
      Toast({ type: 'error', label: message })
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div>
      <div className="d-flex align-items-end flex-wrap" css={{ gap: '1rem', marginBottom: '1rem', padding: 10 }}>
        <div>
          <label className="textGray mb-1">Patient First Name</label>
          <input
            type="text"
            className="form-control"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="First name"
          />
        </div>
        <div>
          <label className="textGray mb-1">Patient Last Name</label>
          <input
            type="text"
            className="form-control"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Last name"
          />
        </div>
        <div>
          <label className="textGray mb-1">Balances</label>
          <select
            className="form-control"
            value={balanceType}
            onChange={(e) => setBalanceType(e.target.value as BalanceType)}>
            <option value="ALL">All outstanding</option>
            <option value="OWING">Owing</option>
            <option value="REFUND_DUE">Refund due</option>
          </select>
        </div>
        <div>
          <label className="textGray mb-1">Reminder cycle</label>
          <select
            className="form-control"
            value={cycleFilter}
            onChange={(e) => setCycleFilter(e.target.value as CycleFilter)}>
            <option value="ALL">All</option>
            <option value={BillingStatementCycleStatus.ACTIVE}>In cycle</option>
            <option value={BillingStatementCycleStatus.COLLECTIONS_REVIEW}>Collections review</option>
            <option value={BillingStatementCycleStatus.PAYMENT_PLAN}>Payment plan</option>
            <option value={BillingStatementCycleStatus.COLLECTIONS}>Collections</option>
            <option value="PAPER_DUE">Paper statement due</option>
          </select>
        </div>
        <div>
          <label className="textGray mb-1">Not statemented in (days)</label>
          <input
            type="number"
            min="1"
            className="form-control"
            value={notSentDays}
            onChange={(e) => setNotSentDays(e.target.value)}
            placeholder="e.g. 30"
          />
        </div>
        <div css={{ marginLeft: 'auto', position: 'relative', display: 'flex', gap: '0.5rem' }}>
          <Dropdown
            additionalText="Records"
            buttonTypeClass="btn-outline-primary"
            initialValue={String(pageSize)}
            onClick={(v: string) => {
              // The selection follows the filters, not the page window, so
              // changing how many rows are shown no longer discards it.
              setPage(0)
              setPageSize(Number(v))
            }}
            options={[
              { label: '12' },
              { label: '20' },
              { label: '50' },
              { label: '100' },
            ]}
          />
          <button className="btn btn-outline-primary" disabled={isExporting} onClick={handleExportCsv}>
            {isExporting ? 'Exporting…' : 'Export CSV'}
          </button>
          <button
            className="btn btn-outline-primary"
            disabled={isPrintingSummary}
            onClick={handlePrintSummary}>
            {isPrintingSummary ? 'Printing…' : 'Print Summary'}
          </button>
          {!!selected.size && (
            <span className="align-self-center text-muted" css={{ whiteSpace: 'nowrap' }}>
              {selected.size} selected
            </span>
          )}
          <button
            className="btn btn-primary"
            disabled={!selected.size || isSelectingAll || isSending}
            onClick={() => setActiveBatchPopover(!activeBatchPopover)}>
            {sendSelectedLabel()}
          </button>
          <Popover open={activeBatchPopover} setOpen={setActiveBatchPopover}>
            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
              }}>
              <button
                className="btn btn-link text-left"
                disabled={isSending}
                onClick={() => handleBatchSend(BillingPatientStatementDeliveryMethod.SMS)}>
                Send SMS
              </button>
              <button
                className="btn btn-link text-left"
                disabled={isSending}
                onClick={() => handleBatchSend(BillingPatientStatementDeliveryMethod.EMAIL)}>
                Send Email
              </button>
            </div>
          </Popover>
        </div>
      </div>

      <div className="table-responsive" css={{ width: '100%', overflow: 'visible' }}>
        <table className="table table-sm" style={{ width: '100%', tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th style={{ width: 40 }}>
                <input
                  type="checkbox"
                  aria-label="Select every matching patient"
                  title="Select every patient matching the current filters, across all pages"
                  disabled={!selectable.length || isSelectingAll}
                  checked={!!selected.size && pageFullySelected}
                  ref={(node) => {
                    // Part-selected reads as neither on nor off.
                    if (node) {
                      node.indeterminate = !!selected.size && !pageFullySelected
                    }
                  }}
                  onChange={toggleSelectAll}
                />
              </th>
              <th>Patient Name</th>
              <th>Patient Balance</th>
              <th>Unapplied Credit</th>
              <th>Refund Due</th>
              <th>Due Date</th>
              <th>Reminder Cycle</th>
              <th>Delivery Method &amp; Status</th>
              <th>Last Sent</th>
              <th style={{ width: 60 }} />
            </tr>
          </thead>
          <tbody>
            {isFetching && (
              <tr>
                <td colSpan={10} className="text-center">
                  Loading...
                </td>
              </tr>
            )}
            {!isFetching && !data?.items?.length && (
              <tr>
                <td colSpan={10} className="text-center">
                  No outstanding balances
                </td>
              </tr>
            )}
            {data?.items?.map((item: OutstandingPatientBalanceItem) => (
              <tr key={item.patientId}>
                <td>
                  <input
                    type="checkbox"
                    disabled={!item.statementId}
                    title={item.statementId ? undefined : 'No statement to send yet'}
                    checked={Boolean(item.statementId && selected.has(item.statementId))}
                    onChange={() => item.statementId && toggleRow(item.statementId, item.patientId)}
                  />
                </td>
                <td>
                  <Link
                    to={`${routes.index}${routes.patient.root}/${item.patientId}`}
                    style={{ textDecoration: 'none' }}>
                    {[item.patientLastName, item.patientFirstName].filter(Boolean).join(', ') || '--'}
                  </Link>
                  {item.hasUnbilledCrossover && (
                    <span css={{ marginLeft: 6 }}>
                      <StatePill
                        color="#3CAEFF"
                        label="Crossed over"
                        tooltip={'A payer forwarded one of this patient\'s claims to a payer the charge '
                          + 'does not list, so part of this balance is money that payer is about to pay. '
                          + 'Add the payer to the charge before sending a statement.'}
                      />
                    </span>
                  )}
                </td>
                <td>{formatMoney(item.patientBalance)}</td>
                <td>{formatMoney(item.unappliedAmount)}</td>
                <td>
                  {item.refundDue > 0 && (
                    <span
                      css={{
                        backgroundColor: '#fde0e0',
                        color: '#dc3545',
                        fontWeight: 600,
                        borderRadius: '4px',
                        padding: '0.1rem 0.4rem',
                      }}>
                      {formatMoney(item.refundDue)}
                    </span>
                  )}
                  {/* Without this the row shows credit against a dash, which
                      reads as a bug. */}
                  {item.refundDue <= 0 && item.refundHeld && (
                    <StatePill
                      color="#6c757d"
                      label="On hold"
                      tooltip={'This credit is not refundable yet — the patient has an order '
                        + 'still waiting on its charge, or a charge that has not settled, that '
                        + 'the credit is expected to pay for. A pre-payment stays on hold until '
                        + 'its charge is created and settled.'}
                    />
                  )}
                  {item.refundDue <= 0 && !item.refundHeld && '--'}
                </td>
                <td>{formatDate(item.statementDueDate)}</td>
                <td>{renderCycle(item)}</td>
                <td>{renderDelivery(item)}</td>
                <td>{formatDateTime(item.lastSentAt)}</td>
                <td style={{ position: 'relative' }}>
                  {(item.statementId || item.cycleStatus || item.patientBalance > 0) && (
                    <>
                      <button
                        className="btn btn-link p-0"
                        onClick={() =>
                          setActiveRowPopover(
                            activeRowPopover === item.patientId ? null : item.patientId,
                          )
                        }>
                        <i className="fas fa-ellipsis-h" />
                      </button>
                      <Popover
                        open={activeRowPopover === item.patientId}
                        setOpen={(open) => {
                          setActiveRowPopover(open ? item.patientId : null)
                        }}>
                        <div
                          css={{
                            display: 'flex',
                            flexDirection: 'column',
                          }}>
                          {item.statementId && (
                            <>
                              <button
                                className="btn btn-link text-left"
                                onClick={() =>
                                  handleSend(item, BillingPatientStatementDeliveryMethod.SMS)
                                }>
                                Send SMS
                              </button>
                              <button
                                className="btn btn-link text-left"
                                onClick={() =>
                                  handleSend(item, BillingPatientStatementDeliveryMethod.EMAIL)
                                }>
                                Send Email
                              </button>
                              <button
                                className="btn btn-link text-left"
                                onClick={() => handleDownload(item)}>
                                Download PDF
                              </button>
                            </>
                          )}
                          {item.cycleStatus && (
                            <>
                              <div className="dropdown-divider" />
                              {CYCLE_STATUS_ACTIONS.filter(
                                (status) => status !== item.cycleStatus,
                              ).map((status) => (
                                <button
                                  key={status}
                                  className="btn btn-link text-left"
                                  onClick={() => handleSetCycleStatus(item, status)}>
                                  Mark {BILLING_STATEMENT_CYCLE_STATUS_LABEL[status].toLowerCase()}
                                </button>
                              ))}
                            </>
                          )}
                          {/* A balance whose cycle ended is only worked again
                              when a biller says so, so this is the way back in
                              for one that was referred or closed. */}
                          {!item.cycleStatus && item.patientBalance > 0 && (
                            <>
                              <div className="dropdown-divider" />
                              <button
                                className="btn btn-link text-left"
                                onClick={() =>
                                  handleSetCycleStatus(item, BillingStatementCycleStatus.ACTIVE)
                                }>
                                Start a reminder cycle
                              </button>
                            </>
                          )}
                        </div>
                      </Popover>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        onPageChange={setPage}
        totalCount={data?.totalItems ?? 0}
        siblingCount={1}
        currentPage={page}
        pageSize={pageSize}
        className=""
        setPageSize={(v: number) => {
          setPage(0)
          setPageSize(v)
        }}
      />
    </div>
  )
}
